import { Component, OnInit, OnDestroy, HostListener } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { AuthService } from 'src/app/services/auth.service';
import { DashboardService } from 'src/app/services/dashboard.service';
import { UserService } from 'src/app/services/user.service';
import { ProviderService } from 'src/app/services/provider.service';
import { ProcedureService } from 'src/app/services/procedure.service';
import { CompanyService } from 'src/app/services/company.service';
import { ContextService } from 'src/app/services/context.service';
import { ModulesService } from 'src/app/services/modules.service';
import { Store } from '@ngrx/store';
import { forkJoin, Subscription } from 'rxjs';
import { distinctUntilChanged, skip } from 'rxjs/operators';
import { ExportService } from 'src/app/layouts/main-v2/services/export.service';
import { IError } from 'src/app/models/error.model';
import { setWarningText } from 'src/app/reducers/warning-popup.reducer';
import { IProvider } from 'src/app/models/provider.model';
import { ICompany } from 'src/app/models/company.model';
import { 
  DashboardFilters, 
  FilteredDashboardData, 
  GeneralDashboardData,
  PeriodFilter,
  SingleValueData
} from 'src/app/models/dashboard.model';
import { format, subDays, subMonths, parseISO } from 'date-fns';

const DASHBOARD_FILTERS_KEY = 'dashboard_v2_filters';

interface DashboardSavedFilters {
  period: PeriodFilter;
  responsibleId: string;
  customStartDate: string;
  customEndDate: string;
  selectedClinicCnpj: string | null;
}

@Component({
  selector: 'app-dashboard-v2',
  templateUrl: './dashboard.component.html',
})
export class DashboardV2Component implements OnInit, OnDestroy {
  loading = true;
  loadingGeneral = true;
  exporting = false;
  showExportModal = false;
  error: string | null = null;

  // Tooltip de informação dos cards
  activeTooltip: string | null = null;
  tooltipPosition: { top: number; left: number } = { top: 0, left: 0 };
  tooltipArrowOffset: number = 50; // percentage for arrow position
  cardTooltips: Record<string, string> = {
    'faturamento-bruto': 'Soma de todos os valores dos procedimentos realizados no período selecionado, sem descontar ressarcimentos.',
    'ressarcimentos': 'Valores pagos à titulo de indenização/acordo com pacientes.',
    'qtd-ressarcimentos': 'Quantidade total de ressarcimentos para pacientes registrados no período selecionado.',
    'faturamento-real': 'Valor do faturamento bruto descontando o total de ressarcimentos. Representa o valor bruto real faturado no período.'
  };
  tooltipTitles: Record<string, string> = {
    'faturamento-bruto': 'Faturamento Bruto',
    'ressarcimentos': 'Ressarcimentos',
    'qtd-ressarcimentos': 'Qtd. Ressarcimentos',
    'faturamento-real': 'Faturamento Real'
  };
  
  // Contagens
  usersCount = 0;
  providersCount = 0;
  proceduresCount = 0;
  patientsCount = 0;
  
  // Dados do dashboard da API
  filteredData: FilteredDashboardData | null = null;
  generalData: GeneralDashboardData | null = null;
  
  // Dados calculados
  anamnesesCount = 0;
  signaturesCount = 0;
  signaturesSigned = 0;
  signaturesUnsigned = 0;
  signaturesPendingResponsible = 0;
  signaturesPendingPatient = 0;
  totalRevenue = 0;
  averageTicket = 0;
  averagePatientAge = 0;
  totalRefundAmount = 0;
  totalRefundCount = 0;
  netRevenue = 0;
  
  // Top procedimentos
  topProcedures: string = '';
  
  // Dados para gráficos
  originRankingData: SingleValueData[] = [];
  procedureRankingData: SingleValueData[] = [];
  revenueHistoryData: { month: string; value: number }[] = [];
  ticketHistoryData: { month: string; value: number }[] = [];
  
  // Filtros
  providers: IProvider[] = [];
  companies: ICompany[] = [];
  filteredCompanies: ICompany[] = [];
  isClinicDropdownOpen = false;
  selectedClinic: ICompany | null = null;
  showCustomDateFields = false;
  // Custom dropdowns state
  isPeriodDropdownOpen = false;
  isResponsibleDropdownOpen = false;
  
  permission: string = '';
  clinicId: string = '';
  isRootUser = false;
  rootActsAsAdmin = false;

  // Formulário de filtros
  periodControl = new FormControl(PeriodFilter.SIXTY_DAYS);
  responsibleControl = new FormControl('');
  customStartDateControl = new FormControl('');
  customEndDateControl = new FormControl('');
  
  filterForm = new FormGroup({
    period: this.periodControl,
    responsibleId: this.responsibleControl,
    customStartDate: this.customStartDateControl,
    customEndDate: this.customEndDateControl
  });

  periodOptions = [
    { value: PeriodFilter.FIFTEEN_DAYS, label: 'Últimos 15 dias' },
    { value: PeriodFilter.SIXTY_DAYS, label: 'Últimos 60 dias' },
    { value: PeriodFilter.THREE_MONTHS, label: 'Últimos 3 meses' },
    { value: PeriodFilter.SIX_MONTHS, label: 'Últimos 6 meses' },
    { value: PeriodFilter.TWELVE_MONTHS, label: 'Últimos 12 meses' },
    { value: PeriodFilter.CUSTOM, label: 'Escolher data' }
  ];

  // Datas para exportação
  exportInitialDate = '';
  exportFinalDate = '';

  private exportSubscription?: Subscription;
  private clinicSubscription?: Subscription;

  constructor(
    private authService: AuthService,
    private dashboardService: DashboardService,
    private userService: UserService,
    private providerService: ProviderService,
    private procedureService: ProcedureService,
    private companyService: CompanyService,
    private contextService: ContextService,
    private exportService: ExportService,
    private store: Store<{ warningPopup: IError }>,
    public modulesService: ModulesService
  ) {
    this.permission = this.authService.getPermission();
    this.clinicId = this.authService.getClinicId();
    this.isRootUser = this.permission === 'ROOT';
  }

  ngOnInit(): void {
    // Carrega filtros salvos do localStorage
    this.loadSavedFilters();

    // Verifica se ROOT já tem clínica ativa (ex: navegou de outra página com clínica selecionada)
    const initialClinic = this.contextService.getActiveClinicSnapshot();
    this.rootActsAsAdmin = this.isRootUser && !!initialClinic?.cnpj;
    
    // Carrega prestadores se não for ROOT, ou se ROOT está atuando como ADMIN
    if (!this.isRootUser || this.rootActsAsAdmin) {
      this.loadProviders();
    }
    
    // Carrega clínicas se for ROOT em modo ROOT (sem clínica ativa)
    if (this.isRootUser && !this.rootActsAsAdmin) {
      this.loadCompanies();
    }
    
    this.loadAllData();

    // Escuta mudanças de clínica ativa (ContextService)
    // skip(1) ignora o valor inicial (já carregado acima), reage apenas a trocas posteriores
    this.clinicSubscription = this.contextService.getActiveClinic().pipe(
      skip(1),
      distinctUntilChanged((a, b) => a?.cnpj === b?.cnpj)
    ).subscribe((clinic) => {
      const wasActingAsAdmin = this.rootActsAsAdmin;
      this.rootActsAsAdmin = this.isRootUser && !!clinic?.cnpj;

      // Troca de modo ROOT ↔ ADMIN: recarrega filtros laterais
      if (this.isRootUser && wasActingAsAdmin !== this.rootActsAsAdmin) {
        if (this.rootActsAsAdmin) {
          // Entrou em modo clínica: carrega prestadores, limpa filtro de clínica do dropdown ROOT
          this.selectedClinic = null;
          this.responsibleControl.setValue('', { emitEvent: false });
          this.loadProviders();
        } else {
          // Voltou pro modo ROOT: carrega clínicas, limpa prestadores
          this.providers = [];
          this.responsibleControl.setValue('', { emitEvent: false });
          this.loadCompanies();
        }
      } else if (this.rootActsAsAdmin) {
        // Trocou de uma clínica para outra (sem mudar de modo): recarrega prestadores
        this.responsibleControl.setValue('', { emitEvent: false });
        this.loadProviders();
      }

      this.clearDashboardData();
      this.loadAllData();
    });

    // Escuta mudanças no filtro de período
    this.periodControl.valueChanges.subscribe((value) => {
      this.showCustomDateFields = value === PeriodFilter.CUSTOM;
      this.saveFiltersToLocalStorage();
      if (value !== PeriodFilter.CUSTOM) {
        this.loadDashboardData();
      }
    });
    
    // Escuta mudanças no filtro de prestador
    this.responsibleControl.valueChanges.subscribe(() => {
      this.saveFiltersToLocalStorage();
      if (this.periodControl.value !== PeriodFilter.CUSTOM) {
        this.loadDashboardData();
      }
    });
    
    // Escuta mudanças nas datas customizadas
    this.customStartDateControl.valueChanges.subscribe(() => {
      this.saveFiltersToLocalStorage();
    });
    
    this.customEndDateControl.valueChanges.subscribe(() => {
      this.saveFiltersToLocalStorage();
    });

    // Escuta requisições de export vindas do header
    this.exportSubscription = this.exportService.exportTrigger$.subscribe(() => {
      this.openExportModal();
    });
  }

  ngOnDestroy(): void {
    this.exportSubscription?.unsubscribe();
    this.clinicSubscription?.unsubscribe();
    // Limpa os filtros ao sair da tela (navegação normal)
    // No reload (F5), o ngOnDestroy não é executado antes do reload, então os filtros são mantidos
    this.clearSavedFilters();
  }

  // ========== FILTROS - LOCALSTORAGE ==========
  private clearSavedFilters(): void {
    try {
      localStorage.removeItem(DASHBOARD_FILTERS_KEY);
    } catch (e) {
      console.warn('Erro ao limpar filtros do dashboard:', e);
    }
  }

  private loadSavedFilters(): void {
    try {
      const saved = localStorage.getItem(DASHBOARD_FILTERS_KEY);
      if (saved) {
        const filters: DashboardSavedFilters = JSON.parse(saved);
        
        // Restaura período
        if (filters.period) {
          this.periodControl.setValue(filters.period, { emitEvent: false });
          this.showCustomDateFields = filters.period === PeriodFilter.CUSTOM;
        }
        
        // Restaura prestador
        if (filters.responsibleId) {
          this.responsibleControl.setValue(filters.responsibleId, { emitEvent: false });
        }
        
        // Restaura datas customizadas
        if (filters.customStartDate) {
          this.customStartDateControl.setValue(filters.customStartDate, { emitEvent: false });
        }
        if (filters.customEndDate) {
          this.customEndDateControl.setValue(filters.customEndDate, { emitEvent: false });
        }
        
        // Restaura clínica selecionada (será aplicada após loadCompanies)
        if (filters.selectedClinicCnpj && this.isRootUser) {
          // Armazena temporariamente para aplicar depois que as clínicas forem carregadas
          this._pendingClinicCnpj = filters.selectedClinicCnpj;
        }
      }
    } catch (e) {
      console.warn('Erro ao carregar filtros salvos do dashboard:', e);
    }
  }

  private _pendingClinicCnpj: string | null = null;

  private saveFiltersToLocalStorage(): void {
    try {
      const filters: DashboardSavedFilters = {
        period: this.periodControl.value as PeriodFilter,
        responsibleId: this.responsibleControl.value || '',
        customStartDate: this.customStartDateControl.value || '',
        customEndDate: this.customEndDateControl.value || '',
        selectedClinicCnpj: this.selectedClinic?.cnpj || null
      };
      localStorage.setItem(DASHBOARD_FILTERS_KEY, JSON.stringify(filters));
    } catch (e) {
      console.warn('Erro ao salvar filtros do dashboard:', e);
    }
  }

  private loadProviders(): void {
    this.providerService.listProviders(0, 100, 'ASC', 'name')
      .subscribe({
        next: (response: any) => {
          this.providers = response?.content || [];
        },
        error: () => this.providers = []
      });
  }

  private loadCompanies(): void {
    this.companyService.listCompanies(0, 9999, 'asc', 'corporateName', '')
      .subscribe({
        next: (response: any) => {
          this.companies = response?.data || [];
          this.filteredCompanies = [...this.companies];
          
          // Restaura clínica selecionada após carregar a lista
          if (this._pendingClinicCnpj) {
            const savedClinic = this.companies.find(c => c.cnpj === this._pendingClinicCnpj);
            if (savedClinic) {
              this.selectedClinic = savedClinic;
            }
            this._pendingClinicCnpj = null;
          }
        },
        error: () => {
          this.companies = [];
          this.filteredCompanies = [];
        }
      });
  }

  /** Limpa todos os dados do dashboard para evitar exibir dados da clínica anterior durante o loading */
  private clearDashboardData(): void {
    this.filteredData = null;
    this.generalData = null;
    this.originRankingData = [];
    this.procedureRankingData = [];
    this.revenueHistoryData = [];
    this.ticketHistoryData = [];
    this.patientsCount = 0;
    this.anamnesesCount = 0;
    this.signaturesCount = 0;
    this.signaturesSigned = 0;
    this.signaturesUnsigned = 0;
    this.signaturesPendingResponsible = 0;
    this.signaturesPendingPatient = 0;
    this.totalRevenue = 0;
    this.averageTicket = 0;
    this.averagePatientAge = 0;
    this.totalRefundAmount = 0;
    this.totalRefundCount = 0;
    this.netRevenue = 0;
    this.usersCount = 0;
    this.providersCount = 0;
    this.proceduresCount = 0;
    this.topProcedures = '';
    this.error = null;
  }

  private loadAllData(): void {
    this.loading = true;
    
    // Carrega contagens das entidades
    this.loadCounts();
    
    // Carrega dados do dashboard
    this.loadDashboardData();
  }

  private loadCounts(): void {
    // Usuários
    if (this.canViewUsers()) {
      this.userService.listUsers(0, 1, 'ASC', 'name')
        .subscribe({
          next: (response: any) => {
            this.usersCount = response?.page_info?.total_elements || 0;
          },
          error: () => this.usersCount = 0
        });
    }

    // Prestadores
    if (this.canViewProviders()) {
      this.providerService.listProviders(0, 1, 'ASC', 'name')
        .subscribe({
          next: (response: any) => {
            this.providersCount = response?.page_info?.total_elements || 0;
          },
          error: () => this.providersCount = 0
        });
    }

    // Procedimentos
    if (this.canViewProcedures()) {
      this.procedureService.listProcedures(0, 1, 'description', 'ASC', '')
        .subscribe({
          next: (response: any) => {
            this.proceduresCount = response?.page_info?.total_elements || 0;
          },
          error: () => this.proceduresCount = 0
        });
    }
  }

  private buildFilters(): DashboardFilters {
    const dates = this.calculateDates();
    this.exportInitialDate = format(new Date(dates.initialDate), 'dd/MM/yyyy');
    this.exportFinalDate = format(new Date(dates.finalDate), 'dd/MM/yyyy');

    // Usa o contexto ativo do ContextService; para ROOT com filtro manual, usa a clínica selecionada
    const activeClinic = this.contextService.getActiveClinicSnapshot();
    const clinicIdToUse = this.rootActsAsAdmin
      ? (activeClinic?.cnpj || '')
      : this.isRootUser 
        ? (this.selectedClinic?.cnpj || '') 
        : (activeClinic?.cnpj || this.authService.getClinicCnpj());

    const filters: DashboardFilters = {
      clinic_id: clinicIdToUse,
      initial_date: dates.initialDate,
      final_date: dates.finalDate
    };

    const responsibleId = this.responsibleControl.value;
    if (responsibleId) {
      filters.responsible_id = responsibleId;
    }

    return filters;
  }

  private loadDashboardData(): void {
    this.loading = true;
    this.loadingGeneral = true;
    this.error = null;
    
    const filters = this.buildFilters();

    // Carregamento paralelo dos dados filtrados e gerais
    forkJoin({
      filtered: this.dashboardService.getFilteredData(filters),
      general: this.dashboardService.getGeneralData(filters)
    }).subscribe({
      next: ({ filtered, general }) => {
        this.filteredData = filtered;
        this.generalData = general;
        
        // Atualiza dados calculados
        this.patientsCount = this.filteredData?.total_patients || 0;
        this.anamnesesCount = this.filteredData?.total_sent_anamnesis || 0;
        this.signaturesCount = this.filteredData?.total_documents || 0;
        this.signaturesSigned = this.filteredData?.total_signed_documents || 0;
        this.signaturesUnsigned = this.filteredData?.total_pending_documents || 0;
        this.signaturesPendingResponsible = this.filteredData?.total_pending_by_responsible_documents || 0;
        this.signaturesPendingPatient = this.filteredData?.total_pending_by_patient_documents || 0;
        this.totalRevenue = this.filteredData?.total_revenue || 0;
        this.averageTicket = this.filteredData?.average_ticket || 0;
        this.averagePatientAge = this.generalData?.average_patient_age || 0;
        this.totalRefundAmount = this.filteredData?.total_refund_amount || 0;
        this.totalRefundCount = this.filteredData?.total_refund_count || 0;
        this.netRevenue = this.filteredData?.net_revenue || 0;
        
        // Prepara dados para gráficos
        this.prepareChartData();
        
        this.loading = false;
        this.loadingGeneral = false;
      },
      error: (error) => {
        console.error('Erro ao carregar dashboard:', error);
        this.error = 'Erro ao carregar dados do dashboard. Tente novamente.';
        this.loading = false;
        this.loadingGeneral = false;
      }
    });
  }

  private prepareChartData(): void {
    // Ranking de origem
    if (this.filteredData?.origin_ranking) {
      this.originRankingData = this.filteredData.origin_ranking.map(([name, value]) => ({
        name: name || 'Não informado',
        value: value
      }));
    }
    
    // Ranking de procedimentos
    if (this.filteredData?.procedure_ranking) {
      this.procedureRankingData = this.filteredData.procedure_ranking.map(p => ({
        name: p.name,
        value: p.value
      }));
      
      // Top procedimentos texto
      if (this.filteredData.procedure_ranking.length > 0) {
        this.topProcedures = this.filteredData.procedure_ranking
          .slice(0, 3)
          .map(p => p.name)
          .join(', ');
        if (this.filteredData.procedure_ranking.length > 3) {
          this.topProcedures += ' e outros.';
        }
      }
    }
    
    // Histórico de faturamento
    if (this.generalData?.total_revenue_history) {
      this.revenueHistoryData = this.generalData.total_revenue_history.map(([month, value]) => ({
        month,
        value
      }));
    }
    
    // Histórico de ticket médio
    if (this.generalData?.average_ticket_history) {
      this.ticketHistoryData = this.generalData.average_ticket_history.map(([month, value]) => ({
        month,
        value
      }));
    }
  }

  private calculateDates(): { initialDate: string, finalDate: string } {
    const period = this.periodControl.value || PeriodFilter.SIXTY_DAYS;
    const now = new Date();
    let initialDate: Date;

    if (period === PeriodFilter.CUSTOM) {
      const customStart = this.customStartDateControl.value;
      const customEnd = this.customEndDateControl.value;
      if (customStart && customEnd) {
        // parseISO interpreta a data como local (sem conversão UTC)
        // new Date("2025-04-01") interpreta como UTC, causando shift de -1 dia no fuso BRT
        return {
          initialDate: format(parseISO(customStart), "yyyy-MM-dd'T'00:00:00.000"),
          finalDate: format(parseISO(customEnd), "yyyy-MM-dd'T'23:59:59.999")
        };
      }
    }

    switch (period) {
      case PeriodFilter.FIFTEEN_DAYS:
        initialDate = subDays(now, 15);
        break;
      case PeriodFilter.SIXTY_DAYS:
        initialDate = subDays(now, 60);
        break;
      case PeriodFilter.THREE_MONTHS:
        initialDate = subMonths(now, 3);
        break;
      case PeriodFilter.SIX_MONTHS:
        initialDate = subMonths(now, 6);
        break;
      case PeriodFilter.TWELVE_MONTHS:
        initialDate = subMonths(now, 12);
        break;
      default:
        initialDate = subDays(now, 60);
    }

    return {
      initialDate: format(initialDate, "yyyy-MM-dd'T'00:00:00.000"),
      finalDate: format(now, "yyyy-MM-dd'T'23:59:59.999")
    };
  }

  searchWithCustomDates(): void {
    if (this.isSearchButtonEnabled) {
      this.loadDashboardData();
    }
  }

  get isSearchButtonEnabled(): boolean {
    const customStart = this.customStartDateControl.value;
    const customEnd = this.customEndDateControl.value;
    return !!(customStart && customEnd);
  }

  // Dropdown de clínicas
  toggleClinicDropdown(): void {
    this.isClinicDropdownOpen = !this.isClinicDropdownOpen;
  }

  closeClinicDropdown(): void {
    this.isClinicDropdownOpen = false;
  }

  onClinicSearch(event: Event): void {
    const searchTerm = (event.target as HTMLInputElement).value.toLowerCase();
    this.filteredCompanies = this.companies.filter(c => 
      c.corporate_name?.toLowerCase().includes(searchTerm) ||
      c.cnpj?.toLowerCase().includes(searchTerm)
    );
  }

  onClinicSelect(clinic: ICompany | null): void {
    this.selectedClinic = clinic;
    this.saveFiltersToLocalStorage();
    this.closeClinicDropdown();
    this.loadDashboardData();
  }

  // Period dropdown
  togglePeriodDropdown(): void {
    this.isPeriodDropdownOpen = !this.isPeriodDropdownOpen;
  }

  selectPeriod(option: { value: PeriodFilter; label: string }): void {
    this.periodControl.setValue(option.value);
    this.isPeriodDropdownOpen = false;
  }

  getSelectedPeriodLabel(): string {
    const option = this.periodOptions.find(o => o.value === this.periodControl.value);
    return option ? option.label : '';
  }

  // Responsible (prestador) dropdown
  toggleResponsibleDropdown(): void {
    this.isResponsibleDropdownOpen = !this.isResponsibleDropdownOpen;
  }

  selectResponsible(provider: IProvider | null): void {
    this.responsibleControl.setValue(provider ? String(provider.id) : '');
    this.isResponsibleDropdownOpen = false;
    if (this.periodControl.value !== PeriodFilter.CUSTOM) {
      this.loadDashboardData();
    }
  }

  getSelectedResponsibleLabel(): string {
    const id = this.responsibleControl.value;
    if (!id) return 'Todos os prestadores';
    const p = this.providers.find(x => String(x.id) === String(id));
    return p ? p.name : 'Todos os prestadores';
  }

  getSelectedClinicLabel(): string {
    return this.selectedClinic?.corporate_name || 'Todas as clínicas';
  }

  // Tooltip de informação
  @HostListener('document:click')
  onDocumentClick(): void {
    this.activeTooltip = null;
  }

  toggleTooltip(key: string, event?: MouseEvent): void {
    if (event) {
      event.stopPropagation();
      const button = event.currentTarget as HTMLElement;
      const rect = button.getBoundingClientRect();
      const tooltipWidth = 300;
      const margin = 12;
      const iconCenterX = rect.left + rect.width / 2;
      const viewportWidth = window.innerWidth;

      // Calculate left so tooltip stays within viewport
      let left = iconCenterX - tooltipWidth / 2;
      left = Math.max(margin, Math.min(left, viewportWidth - tooltipWidth - margin));

      // Arrow offset: how far (in %) the arrow should be from the left
      this.tooltipArrowOffset = Math.max(10, Math.min(90, ((iconCenterX - left) / tooltipWidth) * 100));

      this.tooltipPosition = {
        top: rect.top,
        left
      };
    }
    this.activeTooltip = this.activeTooltip === key ? null : key;
  }

  closeTooltip(): void {
    this.activeTooltip = null;
  }

  // Exportação
  openExportModal(): void {
    this.showExportModal = true;
  }

  closeExportModal(): void {
    this.showExportModal = false;
  }

  confirmExport(): void {
    this.exporting = true;
    // notify header that export started
    this.exportService.setExporting(true);
    const filters = this.buildFilters();
    
    this.dashboardService.exportDashboardCsv(filters).subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `dashboard_${format(new Date(), 'yyyy-MM-dd_HH-mm')}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
        // notify header export finished
        this.exportService.setExporting(false);
        this.exporting = false;
        this.closeExportModal();
      },
      error: (error) => {
        console.error('Erro ao exportar:', error);
        this.store.dispatch(setWarningText('Erro ao exportar dados. Tente novamente.'));
        this.exportService.setExporting(false);
        this.exporting = false;
      }
    });
  }

  // Helpers
  getPeriodLabel(): string {
    const period = this.periodControl.value;
    const option = this.periodOptions.find(o => o.value === period);
    return option ? option.label.toLowerCase() : 'nos últimos 60 dias';
  }

  formatCurrency(value: number): string {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  }

  formatNumber(value: number): string {
    return new Intl.NumberFormat('pt-BR').format(value);
  }

  roundNumber(value: number): number {
    return Math.round(value);
  }

  // Permissões
  canViewUsers(): boolean {
    return ['ADMIN'].includes(this.permission) || this.rootActsAsAdmin;
  }
  
  canViewProviders(): boolean {
    return ['ADMIN'].includes(this.permission) || this.rootActsAsAdmin;
  }
  
  canViewProcedures(): boolean {
    return ['ADMIN'].includes(this.permission) || this.rootActsAsAdmin;
  }
  
  canViewPatients(): boolean {
    return ['ADMIN', 'ATTENDANT'].includes(this.permission) || this.rootActsAsAdmin;
  }
  
  canViewAnamneses(): boolean {
    return ['ADMIN', 'ATTENDANT'].includes(this.permission) || this.rootActsAsAdmin;
  }
  
  canViewSignatures(): boolean {
    return ['ADMIN', 'ROOT'].includes(this.permission);
  }
  
  canViewRevenue(): boolean {
    return ['ADMIN', 'ROOT'].includes(this.permission);
  }

  get shouldShowPatientMap(): boolean {
    return !this.isRootUser || this.rootActsAsAdmin;
  }

  get shouldShowClinicMap(): boolean {
    return this.isRootUser && !this.rootActsAsAdmin;
  }
}
